import PropTypes from "prop-types";
import { useRouter } from "next/router";
import { useRef, useState, useEffect, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "next-i18next";
import { addDofollowToAnchors } from "../utils/modifyHtmlLinks";
import Link from "next/link";

const SubServicesList = memo(
  ({ title, subServices, childServices, parentSlug, className = "" }) => {
    const { t } = useTranslation("common");
    const router = useRouter();
    const lang = router.locale || "en";
    const [activeIndex, setActiveIndex] = useState(0);
    const sectionRef = useRef(null);
    const containerRef = useRef(null);
    const scrollRef = useRef([]);
    const [isInView, setIsInView] = useState(false);
    const [paused, setPaused] = useState(false);

    const handleServiceClick = (index) => {
      setActiveIndex(index);
      scrollRef.current[index]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    };

    // useEffect(() => {
    //   if (!subServices || subServices.length === 0) return;

    //   const interval = setInterval(() => {
    //     setActiveIndex((prev) => (prev + 1) % subServices.length);
    //   }, 5000);

    //   return () => clearInterval(interval);
    // }, [subServices]);

    // Detect if the section is in view
    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => setIsInView(entry.isIntersecting),
        { threshold: 0.2 },
      );

      if (sectionRef.current) {
        observer.observe(sectionRef.current);
      }

      return () => {
        if (sectionRef.current) {
          observer.unobserve(sectionRef.current);
        }
      };
    }, []);

    // Auto-scroll when in view and not paused
    useEffect(() => {
      if (!subServices || subServices.length === 0 || !isInView || paused)
        return;

      const interval = setInterval(() => {
        setActiveIndex((prev) => {
          const nextIndex = (prev + 1) % subServices.length;

          setTimeout(() => {
            scrollRef.current[nextIndex]?.scrollIntoView({
              behavior: "smooth",
              inline: "center",
              block: "nearest",
            });
          }, 0);

          return nextIndex;
        });
      }, 5000);

      return () => clearInterval(interval);
    }, [subServices, isInView, paused]);

    return (
      <section
        ref={sectionRef}
        className={`pb-[40px] lg:pb-[60px] w-full px-4 box-border sm:px-6 lg:px-20 text-text-primary max-w-[1600px] mx-auto ${className}`}
      >
        <div className="w-full flex flex-col gap-6 md:gap-10">
          <h2 className="text-[28px] sm:text-[32px] lg:text-[40px] capitalize font-medium leading-[120%] m-0">
            {title}
          </h2>

          <div className="flex flex-col lg:flex-row gap-8 w-full items-center">
            {/* Left: Service Titles */}
            {/* <div
              ref={containerRef}
              className="lg:w-1/4 w-full overflow-y-auto overflow-x-hidden max-h-[400px] lg:max-h-[465px] px-4 py-2 flex flex-col gap-4 scroll-smooth custom_scrollbar2"
            > */}
            <div
              ref={containerRef}
              className="flex overflow-x-hidden lg:overflow-y-auto gap-4 p-1 sm:px-4 scroll-smooth custom_scrollbar max-w-full lg:max-h-[465px] flex-row lg:flex-col snap-x snap-mandatory touch-pan-x"
            >
              {subServices?.map((service, index) => (
                <button
                  key={index}
                  ref={(el) => (scrollRef.current[index] = el)}
                  onClick={() => handleServiceClick(index)}
                  className={`w-max self-start shrink-0 border-none cursor-pointer px-6 text-start py-[12px]  font-medium text-[16px] sm:text-[18px] lg:text-[20px] rounded-full transition-all duration-300 bg-transparent text-black font-medium flex items-center justify-start box-border ${
                    activeIndex === index
                      ? "border border-crimson outline outline-1 outline-crimson ml-2"
                      : "border border-white/30"
                  }`}
                >
                  {service?.point}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="hidden lg:block h-[465px] w-px bg-gray-500" />

            {/* Right: Service Content */}
            <div className="flex-1 flex flex-col gap-6 px-1 sm:px-3 overflow-hidden w-full">
              {subServices?.length > 0 && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col gap-6"
                  >
                    <h3 className=" font-medium text-[24px] sm:text-[30px] lg:text-[40px] leading-[120%] m-0">
                      {subServices[activeIndex]?.point}
                    </h3>
                    <div
                      className=" font-normal text-[14px] sm:text-[16px] leading-[150%] break-words whitespace-pre-wrap m-0 rich-text"
                      dangerouslySetInnerHTML={{
                        __html:
                          addDofollowToAnchors(
                            subServices[activeIndex]?.description,
                          ) || "",
                      }}
                    />
                    {/* {subServices[activeIndex]?.description}
                    </p> */}
                  </motion.div>
                </AnimatePresence>
              )}

              <div className="flex gap-4 flex-wrap">
                <button
                  className="rounded-[50px] bg-black text-white text-[16px] font-medium py-3 px-6 cursor-pointer hover:bg-[#e72c46] transition duration-300 ease-in-out"
                  onClick={() =>
                    router.push("/contact-digital-marketing-agency")
                  }
                >
                  {t("btnTxt.getInTouch")}
                </button>
                {childServices?.length > 0 &&
                  subServices[activeIndex]?.slug_en && (
                    <Link
                      href={`/${parentSlug}/${subServices[activeIndex]?.slug_en}`}
                      className="no-underline rounded-[50px] bg-transparent text-black border border-black border-solid text-[16px] font-medium py-3 px-6 cursor-pointer hover:bg-black hover:text-white transition duration-300 ease-in-out"
                    >
                      {t("btnTxt.learnMore")}
                    </Link>
                  )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  },
);

SubServicesList.propTypes = {
  className: PropTypes.string,
};

export default SubServicesList;
